import type {
  NotificationPortfolio,
  ProductNotificationEligibility,
  ProductNotificationMappingView,
  ProductNotificationPage,
  ProductNotificationTarget,
} from "../types/productNotificationMappings";
import { isNotificationPortfolioId } from "./productUpdateNotifications";

function requireValid(condition: unknown): asserts condition {
  if (!condition) throw new Error("Invalid product notification response");
}

function object(value: unknown): Record<string, unknown> {
  requireValid(value !== null && typeof value === "object" && !Array.isArray(value));
  return value as Record<string, unknown>;
}

function product(value: unknown): NotificationPortfolio {
  const data = object(value);
  requireValid(data.productType === "PORTFOLIO" || data.productType === "BASKET");
  requireValid(typeof data.productId === "number" && isNotificationPortfolioId(data.productId));
  requireValid(data.title === null || typeof data.title === "string");
  let provider: NotificationPortfolio["provider"] = null;
  if (data.provider !== null) {
    const details = object(data.provider);
    requireValid(typeof details.id === "number" && isNotificationPortfolioId(details.id));
    requireValid(typeof details.name === "string");
    requireValid(details.logoUrl === null || typeof details.logoUrl === "string");
    provider = { id: details.id, name: details.name, logoUrl: details.logoUrl };
  }
  return { productType: data.productType, productId: data.productId, title: data.title, provider };
}

function utcDateTime(value: unknown): string {
  requireValid(typeof value === "string");
  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?Z$/.exec(value);
  requireValid(match && Number(match[2]) < 24 && Number(match[3]) < 60 && Number(match[4] ?? 0) < 60);
  const date = new Date(`${match[1]}T00:00:00Z`);
  requireValid(Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === match[1]);
  return value;
}

function mapping(value: unknown): ProductNotificationMappingView["mapping"] {
  if (value === null) return null;
  const data = object(value);
  requireValid(typeof data.shouldEmail === "boolean" && typeof data.shouldWebInbox === "boolean"
    && typeof data.shouldMobileInbox === "boolean" && typeof data.shouldPush === "boolean" && data.shouldSms === false);
  return {
    shouldEmail: data.shouldEmail, shouldWebInbox: data.shouldWebInbox,
    shouldMobileInbox: data.shouldMobileInbox, shouldPush: data.shouldPush, shouldSms: false,
    createdAtUtc: utcDateTime(data.createdAtUtc), updatedAtUtc: utcDateTime(data.updatedAtUtc),
  };
}

function eligibility(value: unknown): ProductNotificationEligibility {
  const data = object(value);
  if (data.eligible === true && data.reason === null) return { eligible: true, reason: null };
  requireValid(data.eligible === false && (data.reason === "PRIVATE"
    || data.reason === "SUBSCRIPTION_REQUIRED" || data.reason === "PRODUCT_UNAVAILABLE"));
  return { eligible: false, reason: data.reason };
}

function mappingView(value: unknown): ProductNotificationMappingView {
  const data = object(value);
  const result = { product: product(data.product), mapping: mapping(data.mapping), eligibility: eligibility(data.eligibility) };
  requireValid(!result.eligibility.eligible || (result.product.title !== null && result.product.provider !== null));
  return result;
}

function page<T>(value: unknown, readItem: (item: unknown) => T): ProductNotificationPage<T> {
  const data = object(value);
  requireValid(typeof data.currentPage === "number" && isNotificationPortfolioId(data.currentPage));
  const count = (value: unknown): number => {
    requireValid(typeof value === "number" && Number.isSafeInteger(value) && value >= 0);
    return value;
  };
  const itemsOnPage = count(data.itemsOnPage);
  const totalPages = count(data.totalPages);
  const totalItems = count(data.totalItems);
  requireValid(Array.isArray(data.items) && data.items.length <= 100 && itemsOnPage === data.items.length);
  requireValid(totalItems >= itemsOnPage && (totalItems === 0 ? totalPages === 0 : totalPages > 0 && totalPages <= totalItems));
  requireValid(data.currentPage <= totalPages || data.items.length === 0);
  return { currentPage: data.currentPage, itemsOnPage, totalPages, totalItems, items: data.items.map(readItem) };
}

function readResponse<T>(read: () => T, message: string): T {
  try { return read(); } catch { throw new Error(message); }
}

export function readMapping(data: unknown, target: ProductNotificationTarget): ProductNotificationMappingView {
  return readResponse(() => {
    const result = mappingView(data);
    requireValid(result.product.productType === target.productType && result.product.productId === target.productId);
    return result;
  }, "Unable to confirm product trade alert settings");
}

export function readMappingPage(data: unknown): ProductNotificationPage<ProductNotificationMappingView> {
  return readResponse(() => page(data, item => {
    const result = mappingView(item);
    requireValid(result.mapping !== null);
    return result;
  }), "Unable to read product trade alert settings");
}

export function readNotificationProductPage(data: unknown): ProductNotificationPage<NotificationPortfolio> {
  return readResponse(() => page(data, item => {
    const result = product(item);
    requireValid(result.title !== null && result.provider !== null);
    return result;
  }), "Unable to read eligible products");
}
